import axios from 'axios';

class ServerAPI {
  constructor() {
    this.isAuthenticated = false;
    this.locationPromise = null;
  }

  // Get user's current location
  async getUserLocation() {
    if (this.locationPromise) {
      return this.locationPromise;
    }

    this.locationPromise = new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            userLat: position.coords.latitude,
            userLon: position.coords.longitude
          });
        },
        (error) => {
          console.error('Geolocation error:', error);
          // Fallback to default location if geolocation fails
          resolve({
            userLat: 0,
            userLon: 0
          });
        },
        {
          timeout: 10000,
          enableHighAccuracy: false
        }
      );
    });

    return this.locationPromise;
  }

  async getOfficeLogo(locationName) {
    try {
      // The logo endpoint now directly serves the image file
      // Return the URL that can be used as src for an img tag
      return `/api/logo/${encodeURIComponent(locationName)}`;
    } catch (error) {
      console.error('Failed to get office logo:', error);
      throw new Error(error.response?.data?.error || 'Failed to get office logo');
    }
  }

  // Get available office codes
  async getOfficeCodes() {
    try {
      const response = await axios.get(`/api/office-codes`);
      return response.data.officeCodes;
    } catch (error) {
      console.error('Failed to get office codes:', error);
      throw new Error(error.response?.data?.error || 'Failed to get office codes');
    }
  }

  // Get legal documents for a location
  async getLegalDocuments(locationName) {
    try {
      const response = await axios.get(`/api/legal-documents/${encodeURIComponent(locationName)}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get legal documents:', error);
      throw new Error(error.response?.data?.error || 'Failed to get legal documents');
    }
  }
  
  // Get legal documents for all locations
  async getAllLegalDocuments() {
    try {
      const response = await axios.get(`/api/legal-documents`);
      return response.data;
    } catch (error) {
      console.error('Failed to get legal documents:', error);
      throw new Error(error.response?.data?.error || 'Failed to get legal documents');
    }
  }

  // Patient lookup api call - checks enrollment status and auto-enrolls if not enrolled anywhere
  async Lookup(patientData, officeCode, consentData = {}) {
    try {
      const response = await axios.post(`/api/lookup`, {
        patientData,
        officeCode,
        consentData,
      });
      
      return response.data;
    } catch (error) {
      // If it's not an HTTP error (no response property), preserve the original error message
      if (!error.response) {
        throw error; // Re-throw the original error to preserve the message
      }
      throw new Error(error.response?.data?.error || 'Patient lookup failed');
    }
  }

  // Complete enrollment api call - combines patient lookup, enrollment status
  async Enroll(patientData, officeCode, consentData = {}) {
    try {
      const response = await axios.post(`/api/Enroll`, {
        patientData,
        officeCode,
        consentData,
      });
      
      return response.data;
    } catch (error) {
      // If it's not an HTTP error (no response property), preserve the original error message
      if (!error.response) {
        throw error; // Re-throw the original error to preserve the message
      }
      throw new Error(error.response?.data?.error || 'Enrollment failed');
    }
  }

  // Download discount card
  async downloadCard(locationName) {
    try {
      const response = await axios.get(`/api/download-card/${encodeURIComponent(locationName)}`, {
        responseType: 'blob'
      });
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mySimpleRX-discount-card.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return true;
    } catch (error) {
      console.error('Failed to download card:', error);
      throw new Error('Failed to download card');
    }
  }

  // Get offers for a location
  async getOffers(locationName) {
    try {
      const response = await axios.get(`/api/offers/${encodeURIComponent(locationName)}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get offers:', error);
      throw new Error(error.response?.data?.error || 'Failed to get offers');
    }
  }

  // Unsubscribe from email communications
  async unsubscribeEmail(email) {
    try {
      const response = await axios.post('/api/unsubscribe/email', {
        email: email.trim().toLowerCase()
      });
      return response.data;
    } catch (error) {
      // console.log('Failed to unsubscribe email:', error);
      throw new Error(error.response?.data?.error || 'Failed to unsubscribe from emails');
    }
  }

  // Unsubscribe from SMS communications
  async unsubscribeSMS(phone) {
    try {
      const response = await axios.post('/api/unsubscribe/sms', {
        phone: phone.replace(/\D/g, '') // Remove all non-digits
      });
      return response.data;
    } catch (error) {
      // console.log('Failed to unsubscribe SMS:', error);
      throw new Error(error.response?.data?.error || 'Failed to unsubscribe from SMS');
    }
  }
}

const serverAPI = new ServerAPI();

// Expose debug method globally for browser console access
if (typeof window !== 'undefined') {
  window.serverAPI = serverAPI;
}

export default serverAPI;
